import { useEffect, useState } from 'react';
import { api, ApiFailure } from '../api';
import { Notice } from './Panels';

interface PortalSummary {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  reportCount: number;
  dashboardCount: number;
  createdAt: string;
}

interface PortalDetail {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  branding: {
    companyName: string;
    logoUrl: string | null;
    accentColor: string;
    primaryColor: string;
    footerText: string | null;
    showDataVantageBranding: boolean;
  };
  config: {
    reportIds: string[];
    dashboardIds: string[];
    allowExport: boolean;
    allowRawData: boolean;
    allowExploration: boolean;
  };
}

interface AccessToken {
  clientEmail: string;
  clientName: string;
  createdAt: string;
  expiresAt: string;
  accessCount: number;
}

/**
 * White-label client portals — manage branded portals for agency clients.
 * Each portal is a customised view over the workspace's reports, accessible
 * via a unique URL with token-based authentication.
 */
export function ClientPortal() {
  const [portals, setPortals] = useState<PortalSummary[]>([]);
  const [selected, setSelected] = useState<PortalDetail | null>(null);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => { refresh(); }, []);

  async function refresh(): Promise<void> {
    try {
      const r = await api.listPortals();
      setPortals(r.portals);
    } catch { setPortals([]); }
  }

  async function viewPortal(id: string): Promise<void> {
    setFailure(null);
    try {
      const r = await api.getPortal(id);
      setSelected(r.portal as PortalDetail);
      setTokens(r.accessTokens);
    } catch (e) { setFailure(asFailure(e)); }
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Client Portals</h2>
        <p>
          Create branded portals for your clients. Each portal is a custom URL where clients
          can view reports and dashboards with your branding — DataVantage is invisible.
        </p>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New portal'}
        </button>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {showCreate && <CreatePortalForm onCreated={() => { setShowCreate(false); void refresh(); }} />}

      {portals.length === 0 && !showCreate && (
        <div className="card empty">
          <h3>No portals yet</h3>
          <p>Create a branded portal for your first client. They get a custom URL with your branding.</p>
        </div>
      )}

      {portals.length > 0 && (
        <section className="card">
          <header><h2>Your portals</h2><span className="sub">{portals.length}</span></header>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Reports</th>
                  <th>Dashboards</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {portals.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.name}</strong><br /><span className="muted" style={{ fontSize: 12 }}>/portal/{p.slug}</span></td>
                    <td><span className={`badge ${p.active ? 'accent' : ''}`}>{p.active ? 'Active' : 'Inactive'}</span></td>
                    <td>{p.reportCount}</td>
                    <td>{p.dashboardCount}</td>
                    <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    <td><button className="ghost tiny" onClick={() => void viewPortal(p.id)}>Manage</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {selected !== null && (
        <section className="card" style={{ marginTop: 16 }}>
          <header>
            <h2>{selected.name}</h2>
            <button className="ghost tiny" onClick={() => { setSelected(null); setTokens([]); }}>Close</button>
          </header>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
            <div>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>Branding</h3>
              <div style={{ fontSize: 13 }}>
                <p><strong>Company:</strong> {selected.branding.companyName}</p>
                <p><strong>Accent:</strong> <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 4, background: selected.branding.accentColor, verticalAlign: 'middle' }} /> {selected.branding.accentColor}</p>
                <p><strong>DataVantage branding:</strong> {selected.branding.showDataVantageBranding ? 'Shown' : 'Hidden'}</p>
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>Permissions</h3>
              <div style={{ fontSize: 13 }}>
                <p>Export: {selected.config.allowExport ? '✓' : '✗'}</p>
                <p>Raw data: {selected.config.allowRawData ? '✓' : '✗'}</p>
                <p>Exploration: {selected.config.allowExploration ? '✓' : '✗'}</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ fontSize: 14 }}>Access tokens</h3>
              <button className="ghost tiny" onClick={() => setShowTokenForm(!showTokenForm)}>
                {showTokenForm ? 'Cancel' : '+ Add client'}
              </button>
            </div>

            {showTokenForm && <CreateTokenForm portalId={selected.id} onCreated={() => { setShowTokenForm(false); void viewPortal(selected.id); }} />}

            {tokens.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Client</th><th>Email</th><th>Created</th><th>Expires</th><th>Views</th></tr>
                  </thead>
                  <tbody>
                    {tokens.map((t) => (
                      <tr key={t.clientEmail}>
                        <td>{t.clientName}</td>
                        <td>{t.clientEmail}</td>
                        <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                        <td>{new Date(t.expiresAt).toLocaleDateString()}</td>
                        <td>{t.accessCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function CreatePortalForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  async function create(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.createPortal({
        name,
        branding: { companyName: companyName || undefined, accentColor },
      });
      onCreated();
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <header><h2>Create portal</h2></header>
      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        <label className="field">Portal name<input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp Client Portal" /></label>
        <label className="field">Company name (shown on portal)<input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your Agency Name" /></label>
        <label className="field">Accent colour<input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} /></label>
        <button className="primary" onClick={create} disabled={busy || name.trim() === ''}>
          {busy ? <span className="spinner" /> : 'Create portal'}
        </button>
      </div>
    </section>
  );
}

function CreateTokenForm({ portalId, onCreated }: { portalId: string; onCreated: () => void }) {
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  async function createToken(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const r = await api.createPortalAccess(portalId, { clientName, clientEmail });
      setToken(r.token);
      setAccessUrl(r.accessUrl);
      onCreated();
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      {token !== null ? (
        <div>
          <Notice tone="good" message="Access link created. Share it with your client — it is shown only once." />
          <div style={{ marginTop: 8, padding: 12, background: 'var(--sunken)', borderRadius: 6, wordBreak: 'break-all', fontSize: 13 }}>
            {accessUrl}
          </div>
          <button className="ghost" style={{ marginTop: 8 }} onClick={() => {
            if (accessUrl !== null) void navigator.clipboard.writeText(accessUrl);
          }}>Copy link</button>
        </div>
      ) : (
        <>
          {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field">Client name<input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="John Smith" /></label>
            <label className="field">Client email<input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="john@client.com" /></label>
            <button className="primary" onClick={createToken} disabled={busy || clientName.trim() === '' || clientEmail.trim() === ''}>
              {busy ? <span className="spinner" /> : 'Generate link'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
